import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, User, Lock, Bell, LogOut, Repeat } from "lucide-react";
import { useToast } from "../components/Toast";
import { roleConfigs, type Role, type RoleConfig } from "./roles";
import { roleLabel, useAuth } from "../context/AuthContext";

export function ProfileMenu({ current }: { current: RoleConfig }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const push = useToast();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function switchRole(r: Role) {
    setOpen(false);
    navigate(roleConfigs[r].pageBase);
    push({ kind: "info", text: `Переключено: ${roleConfigs[r].breadcrumb}` });
  }

  async function doLogout() {
    setOpen(false);
    await logout();
    push({ kind: "warn", text: "Сессия завершена" });
    navigate("/login", { replace: true });
  }

  const fullName = user?.full_name ?? "Гость";
  const login = user?.login ?? "—";
  const initials = initialsOf(fullName);
  const userRoleLabel = user ? roleLabel(user.role) : current.label;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg p-1 pr-2 transition-colors hover:bg-[var(--color-surface-2)]"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-[#3b5fc3] to-[#1f3a8a] text-[11px] font-semibold text-white">
          {initials}
        </span>
        <div className="hidden text-left sm:block">
          <div className="text-[12.5px] font-medium leading-tight text-[var(--color-fg)]">
            {fullName}
          </div>
          <div className="text-[10.5px] leading-tight text-[var(--color-fg-muted)]">
            {userRoleLabel}
          </div>
        </div>
        <ChevronDown
          size={14}
          className={"text-[var(--color-fg-muted)] transition-transform " + (open ? "rotate-180" : "")}
        />
      </button>

      {open && (
        <div className="pop-in absolute right-0 top-[44px] z-30 w-[280px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-1.5 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.18)]">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#3b5fc3] to-[#1f3a8a] text-[12px] font-semibold text-white">
              {initials}
            </span>
            <div>
              <div className="text-[13px] font-medium text-[var(--color-fg)]">{fullName}</div>
              <div className="font-mono text-[11.5px] text-[var(--color-fg-muted)]">{login}</div>
            </div>
          </div>

          <div className="my-1 h-px bg-[var(--color-border)]" />
          <div className="px-3 pb-1 pt-2 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-faint)]">
            Переключить роль
          </div>
          {(Object.keys(roleConfigs) as Role[]).map((r) => (
            <button
              key={r}
              onClick={() => switchRole(r)}
              className={
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors " +
                (current.role === r
                  ? "bg-[var(--color-surface-3)] font-medium text-[var(--color-fg)]"
                  : "text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]")
              }
            >
              <Repeat size={15} className="opacity-70" />
              <span className="flex-1 text-left">{roleConfigs[r].breadcrumb}</span>
              {current.role === r && <span className="text-[10.5px] text-[var(--color-fg-muted)]">сейчас</span>}
            </button>
          ))}

          <div className="my-1 h-px bg-[var(--color-border)]" />
          <MenuItem icon={User} label="Профиль" onClick={() => {
            setOpen(false);
            navigate(current.role === "admin" ? "/admin/settings" : current.role === "dev" ? "/dev/settings" : current.role === "local" ? "/local" : "/client");
          }} />
          <MenuItem icon={Lock} label="Сменить пароль" onClick={() => {
            setOpen(false);
            navigate(current.role === "dev" ? "/dev/settings" : "/admin/settings");
          }} />
          <MenuItem icon={Bell} label="Уведомления" onClick={() => {
            setOpen(false);
            navigate(current.role === "local" ? "/local/log" : "/admin/log");
          }} />
          <div className="my-1 h-px bg-[var(--color-border)]" />
          <MenuItem
            icon={LogOut}
            label="Выйти"
            danger
            onClick={doLogout}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  badge,
  danger,
  onClick,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  badge?: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors " +
        (danger
          ? "text-[var(--color-danger)] hover:bg-[var(--color-danger-bg)]"
          : "text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]")
      }
    >
      <Icon size={15} className="opacity-80" />
      <span className="flex-1 text-left">{label}</span>
      {badge && (
        <span className="rounded-full bg-[var(--color-brand-2)] px-1.5 py-0.5 text-[10px] font-medium text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
